<template>
  <div class="paid-grant-edit">
    <div class="paid-grant-edit__head">
      <div>
        <strong>有休付与ルール</strong>
        <p>
          継続勤務年数と週の所定労働日数に応じた付与日数です。
          <b>法定の日数を下回ることはできません。</b>
        </p>
      </div>
      <CButton
        type="button"
        color="secondary"
        variant="outline"
        size="sm"
        @click="back"
      >自社設定へ戻る</CButton>
    </div>

    <CCard>
      <CCardBody>
        <CAlert v-if="errorMessage" color="danger">{{ errorMessage }}</CAlert>
        <CAlert v-if="successMessage" color="success">{{ successMessage }}</CAlert>

        <div v-if="loading" class="paid-grant-edit__loading">読み込んでいます…</div>

        <template v-else-if="loaded">
          <!--
            **法定の下限を、入力欄のすぐ横に出す。**
            別の場所に表を置いても、入れているときには見ない。
          -->
          <CAlert color="info">
            数字の下に出ているのが<b>法定の下限</b>（労働基準法39条1項・3項、
            比例付与は労基則24条の3）。上回るのは自由ですが、
            下回る値は保存できません。
          </CAlert>

          <div
            v-for="row in rows"
            :key="row.workingDays"
            class="paid-grant-edit__block"
          >
            <h3>
              {{ row.label.title }}
              <small v-if="row.label.note">{{ row.label.note }}</small>
            </h3>

            <div class="paid-grant-edit__grid">
              <label
                v-for="(label, i) in serviceLabels"
                :key="`f-${row.workingDays}-${i}`"
                class="paid-grant-edit__cell"
                :class="{ 'is-below': below(row, i) }"
              >
                <span class="paid-grant-edit__years">{{ label }}年</span>
                <input
                  :id="`grant-${row.workingDays}-${i}`"
                  v-model="row.grantedDays[i]"
                  type="number"
                  min="0"
                  step="0.5"
                  class="form-control"
                  :aria-describedby="`min-${row.workingDays}-${i}`"
                />
                <small
                  :id="`min-${row.workingDays}-${i}`"
                  class="paid-grant-edit__minimum"
                >法定 {{ row.minimumDays[i] }}日</small>
              </label>
            </div>
          </div>

          <!--
            **決定はここに1つだけ。**自社設定のほかの画面と同じ場所に出す。
          -->
          <div class="paid-grant-edit__actions">
            <CButton
              type="button"
              color="secondary"
              variant="outline"
              :disabled="saving"
              @click="back"
            >やめる</CButton>
            <CButton
              type="button"
              color="primary"
              :disabled="saving"
              @click="save"
            >{{ saving ? '保存しています…' : '保存する' }}</CButton>
          </div>
        </template>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import api from '@/services/companySettings'
import { safeErrorDigest, safeResponseMessage } from '@/utils/safeResponseMessage'
import {
  SERVICE_LABELS, toDisplayRows, toPayload, isBelowStatutory
} from '../paidGrantRuleTable'

/**
 * 有休の付与ルールを直す画面。
 *
 * <h2>35区分そろえて送る</h2>
 *
 * 1区分ずつ送れる形にすると<b>区分に穴を作れる。</b>付与は
 * 「勤続以下でいちばん大きい区分」を採るので、6.5年の行が消えると
 * 勤続20年の人が5.5年の行（18日）へ落ちる。
 * <b>落ちたことは画面のどこにも出ない。</b>
 *
 * <h2>法定割れは画面でも見せる</h2>
 *
 * サーバも断るが、<b>入れているそばで分かるほうがよい。</b>
 * 送ってから断られると、どの欄が悪いのか探すことになる。
 */
export default {
  name: 'PaidGrantRuleEdit',

  data () {
    return {
      serviceLabels: SERVICE_LABELS,
      rows: [],
      loading: true,
      loaded: false,
      saving: false,
      errorMessage: '',
      successMessage: ''
    }
  },

  created () {
    this.load()
  },

  methods: {
    async load () {
      this.loading = true
      this.errorMessage = ''
      try {
        const data = await api.getPaidGrantRules()
        this.rows = toDisplayRows(data && data.rows, data && data.statutory_minimum)
        this.loaded = true
      } catch (error) {
        this.errorMessage = '有休付与ルールを読み込めませんでした。'
          + ' 時間をおいて開き直してください。'
        this.$emit('load-error', safeErrorDigest(error))
      } finally {
        this.loading = false
      }
    },

    /** その欄が法定を下回っているか。 */
    below (row, index) {
      return isBelowStatutory(row.grantedDays[index], row.minimumDays[index])
    },

    /** 画面で先に断る。**サーバも同じところで断る。** */
    validate () {
      const empty = []
      const below = []
      this.rows.forEach(row => {
        row.grantedDays.forEach((value, i) => {
          const label = row.label.title + '・勤続' + SERVICE_LABELS[i] + '年'
          if (value === '' || value === null || value === undefined) {
            empty.push(label)
          } else if (isBelowStatutory(value, row.minimumDays[i])) {
            below.push(label + '（法定 ' + row.minimumDays[i] + '日）')
          }
        })
      })
      if (empty.length) {
        return '空欄があります: ' + empty.slice(0, 3).join('、')
          + (empty.length > 3 ? ' ほか' + (empty.length - 3) + '件' : '')
      }
      if (below.length) {
        return '法定の付与日数を下回っています: '
          + below.slice(0, 3).join('、')
          + (below.length > 3 ? ' ほか' + (below.length - 3) + '件' : '')
      }
      return ''
    },

    async save () {
      if (this.saving) return
      const refusal = this.validate()
      if (refusal) { this.errorMessage = refusal; this.successMessage = ''; return }

      this.saving = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        await api.replacePaidGrantRules(toPayload(this.rows))
        this.successMessage = '有休付与ルールを保存しました。'
          + ' 次の付与から反映されます。'
        // **保存できた値を読み直す。**送ったつもりの値ではなく、
        // 入っている値を見せる。
        await this.load()
      } catch (error) {
        this.errorMessage = safeResponseMessage(
          error, '有休付与ルールを保存できませんでした。')
      } finally {
        this.saving = false
      }
    },

    back () {
      this.$router.push({ name: '設定' }).catch(() => {})
    }
  }
}
</script>

<style scoped>
.paid-grant-edit__head {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1rem;
}

.paid-grant-edit__head > div:first-child { flex: 1 1 auto; min-width: 0; }
.paid-grant-edit__head strong { color: #17384d; font-size: 1.05rem; }
.paid-grant-edit__head p {
  margin: 0.2rem 0 0;
  color: #6a7f8c;
  font-size: 0.8rem;
  line-height: 1.6;
}

.paid-grant-edit__loading { color: #5c6873; }

.paid-grant-edit__block {
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}

.paid-grant-edit__block:first-of-type { border-top: 0; padding-top: 0; }

.paid-grant-edit__block h3 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.paid-grant-edit__block h3 small {
  margin-left: 0.5rem;
  color: #7b8e9b;
  font-weight: 400;
  font-size: 0.75rem;
}

.paid-grant-edit__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(6.5rem, 1fr));
  gap: 0.75rem;
}

.paid-grant-edit__cell { display: block; margin: 0; }

.paid-grant-edit__years {
  display: block;
  color: #617887;
  font-size: 0.7rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
}

.paid-grant-edit__minimum {
  display: block;
  margin-top: 0.15rem;
  color: #6a7f8c;
  font-size: 0.7rem;
}

/* **下回っている欄は、入れているそばで分かるようにする。** */
.paid-grant-edit__cell.is-below .form-control {
  border-color: #e55353;
  background: #fdf3f3;
}

.paid-grant-edit__cell.is-below .paid-grant-edit__minimum {
  color: #e55353;
  font-weight: 700;
}

.paid-grant-edit__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #ebedef;
}
</style>
